'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import clsx from 'clsx'
import { SubBrandName } from 'lib/settings/brands/index'
import { subBrands } from 'lib/settings/brands/sub-brands'
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  normalizeUsPhone,
  SMS_FINE_PRINT,
  SMS_OPT_IN_SUBTITLE,
  smsConsentCheckboxCopy,
  SmsOptInSource,
} from '../../utils/smsOptIn'
import Button from '../Button/Button'
import { CheckboxInput, PhoneInput } from '../Form'
import Header from '../Header/Header'
import Link from '../Link/Link'
import Modal, { useModal } from '../Modal/Modal'

import useRecordSmsOptInApi from '@/api/useRecordSmsOptInApi'
import { useRudderstackAnalytics } from '@/hooks/useRudderstackAnalytics'

export const SMS_OPT_IN_MODAL_ID = 'sms-opt-in-modal'
// Standalone SMS-only surface (e.g. queued after the Braze email Welcome modal).
// A distinct id so it can be mounted alongside the footer instance — which keeps
// SMS_OPT_IN_MODAL_ID — without two <Modal>s sharing one open-state key.
export const SMS_ONLY_MODAL_ID = 'sms-only-modal'
// Order-confirmation marketing opt-in surface. Its own id so it never shares
// open-state with the footer/SMS-only instances or the transactional
// order-updates modal ('sms-order-updates-modal'), which is a different feature.
export const SMS_ORDER_CONFIRMATION_MODAL_ID =
  'sms-order-confirmation-opt-in-modal'

// Standalone (pre-submit) copy — shown when the modal opens on its own with no
// email carried in (the return-visit replacement path). Figma 316-11552: the
// subtitle pitches "special offers" (vs the post-capture "exclusive offers").
const SMS_OPT_IN_MODAL_TITLE_LEAD = 'Sign Up For Our'
const SMS_OPT_IN_MODAL_TITLE_ACCENT = 'Exclusive SMS List'
const SMS_OPT_IN_STANDALONE_SUBTITLE =
  'Enter your phone number for special offers, early access to promotions, and travel tips.'

// Post-capture headline — shown when the modal is handed off from an email
// signup (the Braze email Welcome modal or the footer newsletter), i.e. opened
// with a seeded email. Reads as "you're on the email list, now add SMS". Only
// the headline changes; the subtitle (SMS_OPT_IN_SUBTITLE) is shared. Figma
// 316-11569.
const SMS_OPT_IN_POST_CAPTURE_TITLE_LEAD = 'You’re'
const SMS_OPT_IN_POST_CAPTURE_TITLE_ACCENT = 'In!'

// Success screen copy. The headline mirrors the form's title treatment — a dark
// lead word with a green accent — matching the Figma success design.
const SMS_OPT_IN_SUCCESS_TITLE_LEAD = 'You’re'
const SMS_OPT_IN_SUCCESS_TITLE_ACCENT = 'Almost There!'
const SMS_OPT_IN_SUCCESS_SUBTITLE =
  'Reply YES to our text to make it official. Travel tips & exclusive offers await.'

export interface SmsOptInModalProps {
  /**
   * The brand whose SMS marketing program this opt-in writes to.
   */
  brand: SubBrandName
  /**
   * Dialog id this instance registers/opens under. Defaults to the footer
   * SMS opt-in id; pass SMS_ONLY_MODAL_ID for the standalone SMS-only surface
   * so two instances can coexist on one page without colliding.
   */
  id?: string
  /**
   * The touchpoint recorded on the opt-in (backend sms_source).
   */
  source?: SmsOptInSource
  /**
   * GTM generate_lead method label, mirroring the email flow's 'Footer'.
   */
  gtmMethod?: string
  /**
   * Optional callback for standalone surfaces; receives the E.164 phone.
   */
  onSuccess?: (phone: string) => void
}

/**
 * SMS marketing opt-in modal (GSE-12082 capture path). Mount anywhere and open
 * via openDialog(SMS_OPT_IN_MODAL_ID, { email }) — the seeded email is
 * forwarded to the backend for identity stitching. Distinct from
 * SmsSignupModal, which is the transactional order-tracking SMS program.
 */
export default function SmsOptInModal({
  brand,
  id = SMS_OPT_IN_MODAL_ID,
  source = 'FOOTER',
  gtmMethod = 'Footer SMS',
  onSuccess,
}: SmsOptInModalProps) {
  const { control, handleSubmit, setError, reset } = useForm()
  const [isSuccess, setIsSuccess] = useState(false)
  const hideDialog = useModal((state) => state.hideDialog)
  const mutation = useRecordSmsOptInApi()
  const { track, loaded: isAnalyticsLoaded } = useRudderstackAnalytics()
  const isOpen = useModal((state) => state.openedDialogs.includes(id))

  // A seeded email means this modal was handed off from an email signup (the
  // Braze email Welcome modal or the footer newsletter) — show the "You’re In!"
  // post-capture copy. Opened standalone (return-visit) it carries no email, so
  // it shows the "Sign Up For Our Exclusive SMS List" copy. Reactive so the
  // headline is correct the moment the dialog opens.
  const seededEmail = useModal((state) => state.dialogArgs[id]?.email)
  const isPostCapture = !!seededEmail
  const titleLead = isPostCapture
    ? SMS_OPT_IN_POST_CAPTURE_TITLE_LEAD
    : SMS_OPT_IN_MODAL_TITLE_LEAD
  const titleAccent = isPostCapture
    ? SMS_OPT_IN_POST_CAPTURE_TITLE_ACCENT
    : SMS_OPT_IN_MODAL_TITLE_ACCENT
  // Post-capture uses the shared "exclusive offers" subtitle (Figma 316-11569);
  // the standalone pitch uses "special offers" (Figma 316-11552).
  const subtitle = isPostCapture
    ? SMS_OPT_IN_SUBTITLE
    : SMS_OPT_IN_STANDALONE_SUBTITLE

  const subBrand = subBrands[brand]

  const stopPropagation = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
  }, [])

  // Fire the RudderStack "modal shown" event once per open, driven off the modal
  // store's open state rather than the Transition's beforeEnter callback: that
  // callback is a single shot, so if the SDK hadn't loaded yet the event was
  // silently dropped. Here we wait for the SDK (isAnalyticsLoaded) and fire once,
  // resetting on close so a re-open re-fires. Pairs with the generate_lead submit
  // event so we can measure SMS marketing take rate/conversion. Client-side only;
  // nothing is submitted on show, so there's no server-side equivalent.
  const shownEventFiredRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      shownEventFiredRef.current = false
      return
    }
    if (isAnalyticsLoaded && !shownEventFiredRef.current) {
      shownEventFiredRef.current = true
      track?.('sms_mktg_modal_shown', {
        brand,
        source,
      })
    }
  }, [isOpen, isAnalyticsLoaded, track, brand, source])

  const onFormSubmit = useCallback(
    (formData: Record<string, any>) => {
      // NANP validation happens here — PhoneInput's Controller overwrites
      // consumer validate rules with its own 10-digit check.
      const phone = normalizeUsPhone(formData.phone)
      if (!phone) {
        setError('phone', {
          type: 'validate',
          message: 'Please enter a valid US phone number.',
        })
        return
      }

      mutation.mutate(
        { phone, smsSource: source, email: seededEmail },
        {
          onSuccess: () => {
            setIsSuccess(true)

            // Client-side GA4 event; the backend fires its own server-side
            // RudderStack generate_lead (channel: sms) — same split as the
            // footer email flow.
            sendGTMEvent({
              event: 'generate_lead',
              method: gtmMethod,
              ...(seededEmail ? { user_email: seededEmail } : {}),
            })

            onSuccess?.(phone)
          },
        },
      )
    },
    [gtmMethod, mutation, onSuccess, seededEmail, setError, source],
  )

  return (
    <Modal
      id={id}
      className="!px-8 !pb-8 !pt-12"
      dialogPanelClass="!max-w-[343px] md:!max-w-[480px] w-full"
      afterHide={() => {
        setIsSuccess(false)
        mutation.reset()
        reset()
      }}
      content={
        isSuccess ? (
          <div className="text-center">
            <Header
              as="h2"
              overrideClassName
              className="type-headline-1 font-bold text-center text-theme-primary md:text-[40px] md:leading-[44px]"
            >
              {SMS_OPT_IN_SUCCESS_TITLE_LEAD}{' '}
              <span className="text-theme-accessibility">
                {SMS_OPT_IN_SUCCESS_TITLE_ACCENT}
              </span>
            </Header>
            <p className="type-body-3 text-center text-theme-primary mt-4">
              {SMS_OPT_IN_SUCCESS_SUBTITLE}
            </p>
            <div className="mt-8 flex justify-center">
              <Button
                type="button"
                onClick={() => hideDialog(id)}
                className={clsx(
                  'w-full',
                  '!bg-theme-primary !border-theme-primary !text-white',
                  'hover:!opacity-90',
                )}
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form
            // The modal portals to the DOM root, but React submit events still
            // bubble up the component tree — stop them so a host <form> (e.g.
            // NewsletterSignup's) doesn't also submit (GSE-12085).
            onSubmit={(e) => {
              e.stopPropagation()
              handleSubmit(onFormSubmit)(e)
            }}
          >
            <Header
              as="h2"
              overrideClassName
              className="type-headline-1 font-bold text-center text-theme-primary md:text-[40px] md:leading-[44px]"
            >
              {titleLead}{' '}
              <span className="text-theme-accessibility">{titleAccent}</span>
            </Header>
            <p className="type-body-3 text-center text-theme-primary mt-4">
              {subtitle}
            </p>
            <div className="mt-6">
              <PhoneInput
                control={control}
                name="phone"
                usOnly
                placeholder="555 - 555 - 5555"
                rules={{
                  required: {
                    value: true,
                    message: 'Phone number is required.',
                  },
                }}
              />
              <CheckboxInput
                control={control}
                name="consent"
                label={smsConsentCheckboxCopy(subBrand.name)}
                className="[&]:items-start mt-2 md:mt-4"
                rules={{
                  required: {
                    value: true,
                    message: 'Please agree to receive SMS messages to sign up.',
                  },
                }}
              />
            </div>
            {mutation.isError && (
              <p className="text-red-600 text-sm mt-4 text-center">
                {mutation.error.message}
              </p>
            )}
            <div className="mt-6 flex justify-center">
              <Button
                type="submit"
                className={clsx(
                  'w-full',
                  '!bg-theme-secondary !border-theme-secondary !text-theme-primary',
                  'hover:!bg-theme-accessibility hover:!border-theme-accessibility hover:!text-white',
                )}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Signing Up...' : 'Sign Up'}
              </Button>
            </div>
            <div className="mt-6">
              <p className="type-legal-1 text-neutral-600">
                {SMS_FINE_PRINT.body}
                <Link
                  onClick={stopPropagation}
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:no-underline underline-offset-1 !inline"
                  href="/terms"
                  inheritFont
                  inline
                >
                  {SMS_FINE_PRINT.termsLabel}
                </Link>
                {SMS_FINE_PRINT.conjunction}
                <Link
                  onClick={stopPropagation}
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:no-underline underline-offset-1 !inline"
                  href="/privacy"
                  inheritFont
                  inline
                >
                  {SMS_FINE_PRINT.privacyLabel}
                </Link>
                {SMS_FINE_PRINT.suffix}
              </p>
            </div>
          </form>
        )
      }
    />
  )
}
